import noop from 'lodash/noop';

import { Bar, BarContainer, CenterLine, Label, LabelsContainer, OuterContainer } from './styles';

interface VerticalProgressBarProps {
  currentStep: number;
  totalSteps: number;
  fillPercent?: number; // Optional number between 0 and 100 that sets the percent of the progress bar that is filled
  labels?: (string | null)[]; // Optional array of labels beside the progress bar
  labelsMargin?: number; // Option vertical margin in rem for the label bar that defaults to .3rem
  height?: number; // Optional height of the progress bar in rem that defaults to 22.3
  handleLabelClick?: (stepNumber: number) => void;
}

export const VerticalProgressBar = ({
  currentStep,
  totalSteps,
  height = 24,
  labels,
  labelsMargin = 0.2,
  fillPercent,
  handleLabelClick,
}: VerticalProgressBarProps) => {
  // The fill of the progress bar should reach just under the label that corresponds to the current step.
  // The height of the fill thus depends on the vertical margin of the labels bar and the font size.
  const fontSize = 1.123;
  const spaceBetweenLabels = (height - fontSize * totalSteps - 2 * labelsMargin) / (totalSteps - 1);
  const initialOffset = 1;
  let fillHeight = labelsMargin + fontSize * currentStep + spaceBetweenLabels * (currentStep - 1) + initialOffset;

  if (currentStep === totalSteps) {
    fillHeight = height - initialOffset;
  }

  if (typeof fillHeight !== 'number') {
    fillHeight = 0;
  }

  if (fillPercent) {
    fillHeight = (height * fillPercent) / 100;
  }

  const handleClick = (labelStepNum: number, isClickable: boolean) =>
    isClickable && !!handleLabelClick ? handleLabelClick(labelStepNum) : noop;

  return (
    <OuterContainer height={height} aria-live="polite">
      <BarContainer>
        <Bar fillHeight={fillHeight} data-testid="bar" />
        <CenterLine />
      </BarContainer>

      {labels && (
        <LabelsContainer labelsMargin={labelsMargin}>
          {labels.map((label, index) => {
            const isClickable = !!handleLabelClick && index < currentStep;
            const isActive = index < currentStep;

            return (
              <Label
                key={`label${index}`}
                isClickable={isClickable}
                onClick={() => handleClick(index, isClickable)}
                isActive={isActive}
              >
                {label}
              </Label>
            );
          })}
        </LabelsContainer>
      )}
    </OuterContainer>
  );
};
